import {
  DurationPickerV2,
  InfoIconPopover,
  InfoPopover,
  Modal,
  TrackedButton,
  Typography,
  useDurationPickerField,
} from '@automata/ui';
import {
  ExecutorOptionsInput,
  ReplanningOptionsInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Slider,
  Stack,
} from '@mui/material';
import { FC, useMemo, useState } from 'react';

import { selectExecutorOptions } from '../../../../features/workflow/selectors';
import { updateExecutorOptions } from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

const TOOLTIPS = {
  errorHandling: (
    <div>
      If this option is enabled, when a labware is aborted, all labware with the
      same batch value as the affected labware will be aborted automatically.
    </div>
  ),
  replanningOptions: (
    <ul>
      <li>
        <b>Minimise change:</b> adds an optimisation target to minimise
        deviations from the current plan.
      </li>
      <li>
        <b>Under run threshold:</b> defines how much earlier than expected a
        task must be completed to trigger replanning.
      </li>
      <li>
        <b>Over run threshold:</b> defines how much later than planned a task
        must be completed to trigger replanning.
      </li>
      <li>
        <b>Maximum increase factor:</b> limits the maximum increase in the total
        workflow&apos;s makespan during replanning, expressed as a
        multiplicative factor of the original plan. For example, a value of 2.0
        doubles the makespan. Typical configurable range: 1.1 to 2.0.
      </li>
      <li>
        <b>Buffer:</b> specifies the minimum time that must pass between
        consecutive replanning events.
      </li>
    </ul>
  ),
  maximumIncreaseFactor: (
    <div>
      Limits the maximum increase in the total workflow&apos;s makespan during
      replanning, expressed as a multiplicative factor of the original plan. For
      example, a value of 2.0 doubles the makespan. Typical configurable range:
      1.1 to 2.0.
    </div>
  ),
};

type Props = {
  onClose: () => void;
};

export const EditExecutorOptionsModal: FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const previousExecutorOptions = useAppSelector(selectExecutorOptions);

  const [executorOptions, setExecutorOptions] = useState<ExecutorOptionsInput>(
    previousExecutorOptions ?? {}
  );

  const isInitial = useMemo(() => {
    return (
      JSON.stringify(executorOptions) ===
      JSON.stringify(previousExecutorOptions ?? {})
    );
  }, [executorOptions, previousExecutorOptions]);

  const handleUpdateOption = (
    patch:
      | Partial<ExecutorOptionsInput>
      | { replanning_options: Partial<ReplanningOptionsInput> }
  ) => {
    setExecutorOptions((prev) => {
      if ('replanning_options' in patch) {
        return {
          ...prev,
          replanning_options: {
            ...(prev.replanning_options ?? {}),
            ...patch.replanning_options,
          },
        };
      }
      return {
        ...prev,
        ...patch,
      };
    });
  };

  const handleSave = () => {
    dispatch(updateExecutorOptions(executorOptions));
    onClose();
  };

  const {
    breakdown: underrunBreakdown,
    handleUpdateTime: handleUpdateUnderrun,
    handleBlur: handleBlurUnderrun,
  } = useDurationPickerField(
    (val) =>
      handleUpdateOption({
        replanning_options: { replan_underrun_threshold: val },
      }),
    executorOptions.replanning_options?.replan_underrun_threshold
  );

  const {
    breakdown: overrunBreakdown,
    handleUpdateTime: handleUpdateOverrun,
    handleBlur: handleBlurOverrun,
  } = useDurationPickerField(
    (val) =>
      handleUpdateOption({
        replanning_options: { replan_overrun_threshold: val },
      }),
    executorOptions.replanning_options?.replan_overrun_threshold
  );

  const {
    breakdown: bufferBreakdown,
    handleUpdateTime: handleUpdateBuffer,
    handleBlur: handleBlurBuffer,
  } = useDurationPickerField(
    (val) =>
      handleUpdateOption({
        replanning_options: { replan_buffer: val },
      }),
    executorOptions.replanning_options?.replan_buffer
  );

  return (
    <Modal open onClose={onClose} title="Edit executor options">
      <Stack spacing={3} mt={1}>
        <Stack spacing={1}>
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Typography variant="h6">Error handling</Typography>
            <InfoIconPopover
              infoPopover={
                <InfoPopover
                  title="Error handling"
                  copy={TOOLTIPS.errorHandling}
                />
              }
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!executorOptions.abort_labwares_aborts_entire_batch}
                onChange={(e) =>
                  handleUpdateOption({
                    abort_labwares_aborts_entire_batch: e.target.checked,
                  })
                }
              />
            }
            label="Abort all labware in the same batch"
          />
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Typography variant="h6">Replanning options</Typography>
            <InfoIconPopover
              infoPopover={
                <InfoPopover
                  title="Replanning options"
                  copy={TOOLTIPS.replanningOptions}
                />
              }
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  !!executorOptions.replanning_options
                    ?.enable_minimise_change_on_replan
                }
                onChange={(e) =>
                  handleUpdateOption({
                    replanning_options: {
                      enable_minimise_change_on_replan: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Minimise change from current plan"
          />

          <Stack spacing={1}>
            <DurationPickerV2
              label="UNDER RUN THRESHOLD"
              hours={underrunBreakdown.hours}
              minutes={underrunBreakdown.minutes}
              seconds={underrunBreakdown.seconds}
              handleUpdateTime={handleUpdateUnderrun}
              handleBlurParent={handleBlurUnderrun}
            />
            <Typography variant="caption" color="textSecondary">
              Defines how much earlier than expected a task must be completed to
              trigger replanning
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <DurationPickerV2
              label="OVER RUN THRESHOLD"
              hours={overrunBreakdown.hours}
              minutes={overrunBreakdown.minutes}
              seconds={overrunBreakdown.seconds}
              handleUpdateTime={handleUpdateOverrun}
              handleBlurParent={handleBlurOverrun}
            />
            <Typography variant="caption" color="textSecondary">
              Defines how much later than planned a task must be completed to
              trigger replanning
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <DurationPickerV2
              label="BUFFER"
              hours={bufferBreakdown.hours}
              minutes={bufferBreakdown.minutes}
              seconds={bufferBreakdown.seconds}
              handleUpdateTime={handleUpdateBuffer}
              handleBlurParent={handleBlurBuffer}
            />
            <Typography variant="caption" color="textSecondary">
              Specifies the minimum time that must pass between consecutive
              replanning events
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Typography variant="overline">
                MAXIMUM INCREASE FACTOR
              </Typography>
              <InfoIconPopover
                infoPopover={
                  <InfoPopover
                    title="Maximum increase factor"
                    copy={TOOLTIPS.maximumIncreaseFactor}
                  />
                }
              />
            </Box>
            <Slider
              value={
                executorOptions.replanning_options
                  ?.maximum_makespan_increase_factor ?? 1.0
              }
              min={1.0}
              max={2.0}
              step={0.1}
              valueLabelDisplay="auto"
              onChange={(_, v) =>
                handleUpdateOption({
                  replanning_options: {
                    maximum_makespan_increase_factor: v as number,
                  },
                })
              }
            />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption">1.0</Typography>
              <Typography variant="caption">2.0</Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="workflowBuilder.editExecutorOptionsModal.Cancel"
            color="primary"
            variant="text"
            onClick={onClose}
          >
            CANCEL
          </TrackedButton>
          <TrackedButton
            trackLabel="workflowBuilder.editExecutorOptionsModal.Save"
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={isInitial}
          >
            APPLY
          </TrackedButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
